/**
 * Standalone time picker using shadcn Select components (12h format with AM/PM).
 *
 * Accepts and returns "HH:MM" 24h format strings to match existing state interfaces
 * (e.g. PushToScreensDialog reducer). Display is 12-hour with AM/PM toggle.
 *
 * Extracted from the time portion of DateTimePicker.tsx for reuse without date.
 */
import { Clock } from 'lucide-react';
import {
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0, 1, 2, ... 59

function to12Hour(h24: number): { h12: number; period: 'AM' | 'PM' } {
  return { h12: h24 % 12 || 12, period: h24 >= 12 ? 'PM' : 'AM' };
}

function to24Hour(h12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

export interface TimePickerProps {
  /** Time value in "HH:MM" 24h format */
  value: string;
  /** Callback with "HH:MM" 24h format string */
  onChange: (value: string) => void;
  /** Accessible label for the picker */
  'aria-label'?: string;
}

export function TimePicker({ value, onChange, 'aria-label': ariaLabel }: TimePickerProps) {
  const [hStr, mStr] = value.split(':');
  const h24 = parseInt(hStr, 10) || 0;
  const minute = parseInt(mStr, 10) || 0;
  const { h12, period } = to12Hour(h24);

  const emit = (hours24: number, mins: number) => {
    const hh = String(hours24).padStart(2, '0');
    const mm = String(mins).padStart(2, '0');
    onChange(`${hh}:${mm}`);
  };

  return (
    <div className="flex items-center gap-1.5" aria-label={ariaLabel} role="group">
      <Clock className="h-4 w-4 shrink-0 text-foreground" />
      <Select value={String(h12)} onValueChange={(v) => emit(to24Hour(Number(v), period), minute)}>
        <SelectTrigger className="h-8 w-[60px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <ScrollArea className="h-48">
            {HOURS_12.map((h) => (
              <SelectItem key={h} value={String(h)}>
                {h}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      <span className="text-sm text-muted-foreground">:</span>

      <Select value={String(minute)} onValueChange={(v) => emit(h24, Number(v))}>
        <SelectTrigger className="h-8 w-[60px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <ScrollArea className="h-48">
            {MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {String(m).padStart(2, '0')}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>

      <Select value={period} onValueChange={(v) => emit(to24Hour(h12, v as 'AM' | 'PM'), minute)}>
        <SelectTrigger className="h-8 w-[65px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
